import { useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import {
  tldCatalogSchema,
  type TldCatalogFormData,
} from "@/schemas/adminTldCatalog.schema";
import type { TldCatalogDto } from "@/types/domain";

interface TldCatalogFormProps {
  /** When provided, the form is in edit mode and `tld` becomes read-only. */
  tld?: TldCatalogDto;
  onSubmit: (data: TldCatalogFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function TldCatalogForm({
  tld,
  onSubmit,
  onCancel,
  isSubmitting,
}: TldCatalogFormProps) {
  const isEdit = !!tld;

  const form = useForm<TldCatalogFormData>({
    // @ts-expect-error - zodResolver type inference issue with .refine()
    resolver: zodResolver(tldCatalogSchema),
    defaultValues: tld
      ? {
          tld: tld.tld,
          displayName: tld.displayName,
          registrarProvider: tld.registrarProvider,
          registrarCost: tld.registrarCost,
          registrationPrice: tld.registrationPrice,
          renewalPrice: tld.renewalPrice,
          transferPrice: tld.transferPrice,
          restorePrice: tld.restorePrice,
          currency: tld.currency,
          minRegistrationYears: tld.minRegistrationYears,
          maxRegistrationYears: tld.maxRegistrationYears,
          supportsWhoisPrivacy: tld.supportsWhoisPrivacy,
          whoisPrivacyPrice: tld.whoisPrivacyPrice,
          supportsDnsManagement: tld.supportsDnsManagement,
          supportsTransfer: tld.supportsTransfer,
          gracePeriodDays: tld.gracePeriodDays,
          redemptionPeriodDays: tld.redemptionPeriodDays,
          isEnabled: tld.isEnabled,
          isFeatured: tld.isFeatured,
          sortOrder: tld.sortOrder,
        }
      : {
          tld: "",
          displayName: "",
          registrarProvider: "ResellerClub",
          registrarCost: 0,
          registrationPrice: 0,
          renewalPrice: 0,
          transferPrice: 0,
          restorePrice: 0,
          currency: "USD",
          minRegistrationYears: 1,
          maxRegistrationYears: 10,
          supportsWhoisPrivacy: false,
          whoisPrivacyPrice: 0,
          supportsDnsManagement: false,
          supportsTransfer: false,
          gracePeriodDays: 30,
          redemptionPeriodDays: 30,
          isEnabled: true,
          isFeatured: false,
          sortOrder: 0,
        },
  });

  const typedControl = form.control as unknown as Control<TldCatalogFormData>;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium">Basic Information</h3>
          <Separator className="my-3" />
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={typedControl}
                name="tld"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TLD</FormLabel>
                    <FormControl>
                      <Input placeholder=".com" {...field} disabled={isEdit} />
                    </FormControl>
                    <FormDescription>
                      {isEdit
                        ? "The TLD cannot be changed after creation"
                        : 'Must start with a dot, e.g. ".com"'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Dot Com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={typedControl}
                name="registrarProvider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registrar Provider</FormLabel>
                    <FormControl>
                      <Input placeholder="ResellerClub" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input placeholder="USD" {...field} />
                    </FormControl>
                    <FormDescription>3-letter currency code</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div>
          <h3 className="text-lg font-medium">Pricing</h3>
          <Separator className="my-3" />
          <p className="mb-3 text-sm text-muted-foreground">
            All prices are in the smallest currency unit (e.g. cents for USD).
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <FormField
              control={typedControl}
              name="registrarCost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Registrar Cost</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="registrationPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Registration Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="renewalPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Renewal Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="transferPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Transfer Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="restorePrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Restore Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="whoisPrivacyPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>WHOIS Privacy Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>Per year; 0 if bundled free</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Registration Terms */}
        <div>
          <h3 className="text-lg font-medium">Registration Terms</h3>
          <Separator className="my-3" />
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={typedControl}
              name="minRegistrationYears"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Years</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 1)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="maxRegistrationYears"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Years</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 1)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="gracePeriodDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Grace Period (days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="redemptionPeriodDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Redemption Period (days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Capabilities & Visibility */}
        <div>
          <h3 className="text-lg font-medium">Capabilities &amp; Visibility</h3>
          <Separator className="my-3" />
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={typedControl}
              name="supportsWhoisPrivacy"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">WHOIS Privacy</FormLabel>
                    <FormDescription>Offer WHOIS privacy</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="supportsDnsManagement"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">DNS Management</FormLabel>
                    <FormDescription>
                      Full DNS hosting available
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="supportsTransfer"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Transfer-In</FormLabel>
                    <FormDescription>Transfer-in allowed</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="isFeatured"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Featured</FormLabel>
                    <FormDescription>
                      Highlight in search results
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="isEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Enabled</FormLabel>
                    <FormDescription>Sellable to customers</FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={typedControl}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>Lower = first</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? "Update" : "Create"} TLD
          </Button>
        </div>
      </form>
    </Form>
  );
}
